import { LinkedListWithLayoutSettings } from '@Components/LinkedListWithLayoutSettings';
import { PermissionsList } from '@Components/Permissions';
import { PositionsTable } from '@Components/Tables';
import { PERMISSIONS_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { Permission } from '@Constants/Permission.g';
import { externalApplicationSecretColumns, groupColumns } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import {
    useBlockExternalApplication,
    usePermissionsWithGroups,
    useUnblockExternalApplication,
} from '@Hooks/Permissions';
import {
    type ApiExternalApplication,
    type ApiExternalApplicationSecret,
    type ApiGroup,
    EnumAccessState,
    EnumFrontendLayoutSetting,
    ExternalApplicationLinkLists,
} from '@Model';
import {
    ButtonWithConfirmationModal,
    Card,
    Checkbox,
    EditFrameMode,
    EditFrameShell,
    EnumMessageLevel,
    EnumMessageType,
    FrameEditBase,
    type IFrameBodyProps,
    type IFrameEditShellProps,
    TextInput,
    useEditSubObject,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useLinkedList,
    useTranslate,
} from '@synqo/frontend-component-library';
import { useMemo } from 'react';
import { useParams } from 'react-router';
import { FrameEditExternalApplicationSecret } from './FrameEditExternalApplicationSecret';
import { FrameEditGroup } from './FrameEditGroup';
import { getPermissionsColumns } from './getPermissionsColumns';

export function FrameEditExternalApplication(props: IFrameEditShellProps<ApiExternalApplication>) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiExternalApplication>
            apiResourcePath={`${PERMISSIONS_API}/applications`}
            renderer={FrameEditExternalApplicationBody}
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            canEdit={props.canEdit !== false && hasPermission(Permission.PermissionsExternalApplicationsUpdate)}
            canCreate={props.canCreate !== false && hasPermission(Permission.PermissionsExternalApplicationsCreate)}
            canDelete={props.canDelete !== false && hasPermission(Permission.PermissionsExternalApplicationsDelete)}
            frameProps={{
                events: {
                    ...frameEditBaseNavigationEvents,
                    ...props.events,
                },
            }}
        />
    );
}

function FrameEditExternalApplicationBody(props: IFrameBodyProps<ApiExternalApplication>) {
    const i18n = useTranslate('');
    const hasPermission = useHasPermission();

    const baseApiObjectPath = `${props.apiResourcePath}/${props.data.id}`;
    const application = props.data;

    const isNotViewMode = props.mode !== EditFrameMode.View;
    const canEdit = isNotViewMode;
    const isApplicationBlocked = application.access_state === EnumAccessState.Locked;

    const { blockApplicationHandler, blockApplicationIsPending } = useBlockExternalApplication(
        application.id,
        props.refetchData,
    );
    const { unblockApplicationHandler, unblockApplicationIsPending } = useUnblockExternalApplication(
        application.id,
        props.refetchData,
    );

    const binder = useGuiBinder<ApiExternalApplication>({
        ...props,
        canEdit,
    });

    const { permissions, refetch, groups } = usePermissionsWithGroups('applications', application.id);
    const permissionsColumns = useMemo(() => getPermissionsColumns(groups), [groups]);

    const {
        addSubObject: addSecret,
        requestUpdateSubObject: requestUpdateSecret,
        deleteSubObjects: deleteSecrets,
    } = useEditSubObject<ApiExternalApplicationSecret, ApiExternalApplication>(
        baseApiObjectPath,
        'secrets',
        props.requestUpdateState,
    );

    const {
        refetchRef: refetchGroupsRef,
        linkListApi: blankLinkGroupApi,
        linkedObjectOverlayEvents: linkGroupOverlayEvents,
    } = useLinkedList<ApiExternalApplication, ApiGroup>(
        baseApiObjectPath,
        ExternalApplicationLinkLists.groups,
        isNotViewMode && hasPermission(Permission.PermissionsGroupsCreate),
        FrameEditGroup,
    );

    const linkGroupApiWithPermissionRefetch = useMemo(() => {
        const linkGroupAndRefetchPermissions: typeof blankLinkGroupApi.link = async (linkId: string) => {
            const result = await blankLinkGroupApi.link(linkId);
            refetch();
            return result;
        };
        const unlinkGroupAndRefetchPermissions: typeof blankLinkGroupApi.unlink = async (linkId: string) => {
            const result = await blankLinkGroupApi.unlink(linkId);
            refetch();
            return result;
        };

        return {
            ...blankLinkGroupApi,
            link: linkGroupAndRefetchPermissions,
            unlink: unlinkGroupAndRefetchPermissions,
        };
    }, [blankLinkGroupApi, refetch]);

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel('Externe Anwendung', props.mode),
                resourceLabel: 'Externe Anwendung',
                ...props.events,
                actionItems: [
                    application.is_activated &&
                        (isApplicationBlocked ? (
                            <ButtonWithConfirmationModal
                                key="unblock_application_button"
                                onClick={unblockApplicationHandler}
                                locked={unblockApplicationIsPending || isNotViewMode}
                                confirmModalMessage={{
                                    message: 'Wollen Sie die Anwendung wirklich entsperren?',
                                    title: 'Entsperren bestätigen',
                                    type: EnumMessageType.MultipleChoice,
                                    level: EnumMessageLevel.Information,
                                }}
                                variant="secondary"
                            >
                                {unblockApplicationIsPending ? 'Anwendung wird entsperrt...' : 'Anwendung entsperren'}
                            </ButtonWithConfirmationModal>
                        ) : (
                            <ButtonWithConfirmationModal
                                key="block_application_button"
                                onClick={blockApplicationHandler}
                                locked={blockApplicationIsPending || isNotViewMode}
                                confirmModalMessage={{
                                    message:
                                        'Wollen Sie die Anwendung wirklich sperren? Aktive Sitzungen werden sofort blockiert.',
                                    title: 'Sperren bestätigen',
                                    type: EnumMessageType.MultipleChoice,
                                    level: EnumMessageLevel.Warning,
                                }}
                                variant="secondary"
                            >
                                {blockApplicationIsPending ? 'Anwendung wird gesperrt...' : 'Anwendung sperren'}
                            </ButtonWithConfirmationModal>
                        )),
                ],
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card name="PrimaryData" border={'withoutBorder'}>
                <TextInput {...binder.bindToProp<string>('label')} label={'Name'} />
                <TextInput {...binder.bindToProp<string>('application_id')} label={'Application ID'} />
                <Checkbox {...binder.bindToAutoConfirmed('use_staged_import')} label="Import nutzt Schleuse" />
                <TextInput {...binder.bindToProp<string>('remarks')} label={'Bemerkung'} inputSize={3} />
            </Card>

            {application.is_activated && (
                <Card name="secrets" label={`${i18n('Secrets')} (${application.secrets.length})`}>
                    <PositionsTable<ApiExternalApplicationSecret>
                        layoutSettingsContext={EnumFrontendLayoutSetting.ExternalApplicationSecretsTableLayout}
                        items={application.secrets}
                        columns={externalApplicationSecretColumns}
                        name="secrets"
                        editable={isNotViewMode && application.is_activated}
                        addActionProps={{
                            text: 'Secret hinzufügen',
                        }}
                        deleteActionProps={{
                            contextLabel: ['Secret', 'Secrets'],
                        }}
                        canCreate={isNotViewMode}
                        onAdd={addSecret}
                        onDelete={deleteSecrets}
                        renderSubComponent={(row) => (
                            <FrameEditExternalApplicationSecret
                                key={row.id}
                                data={row}
                                meta={undefined}
                                canEdit={isNotViewMode && !row.is_activated}
                                apiResourcePath={`${baseApiObjectPath}/secrets`}
                                requestUpdateState={(u) => requestUpdateSecret(u, row.id)}
                                stateQueueDepth={props.stateQueueDepth}
                                refetchData={props.refetchData}
                            />
                        )}
                    />
                </Card>
            )}

            <Card name="groups" label="Benutzergruppen" maxWidth={'80rem'}>
                <LinkedListWithLayoutSettings<ApiGroup>
                    layoutSettingsContext={EnumFrontendLayoutSetting.GroupsTableLayout}
                    layoutSettingsModalContext={EnumFrontendLayoutSetting.GroupsTableLayout}
                    {...binder.bindToLinkList(linkGroupApiWithPermissionRefetch)}
                    {...linkGroupOverlayEvents}
                    refetchRef={refetchGroupsRef}
                    columns={groupColumns}
                />
            </Card>

            <Card name="permissions" label="Berechtigungen" maxWidth={'80rem'}>
                <p className={'text-right'}>Berechtigungen werden über Benutzergruppen verwaltet.</p>
                <PermissionsList
                    linkedPermissions={permissions.map((obj) => obj.key)}
                    extraColumns={permissionsColumns}
                />
            </Card>
        </EditFrameShell>
    );
}
